import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Plus, ClipboardList, Users, Check, Pencil, Trash2,
  Loader2, Calendar, Award, FileText
} from 'lucide-react';

export default function InstructorAssignmentsPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const sectionId = searchParams.get('section_id');
  
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(sectionId || '');
  const [assignments, setAssignments] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showGradeDialog, setShowGradeDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    due_date: '',
    max_points: 100,
    assignment_type: 'project'
  });

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      fetchData();
    }
  }, [selectedSection]);

  const fetchSections = async () => {
    try {
      const res = await api.get('/instructor/dashboard');
      setSections(res.data.sections || []);
      if (!selectedSection && res.data.sections?.length > 0) {
        setSelectedSection(res.data.sections[0].id);
      }
    } catch (error) {
      toast.error('Failed to load sections');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignmentsRes, rosterRes] = await Promise.all([
        api.get(`/assignments/?section_id=${selectedSection}`),
        api.get(`/instructor/roster?section_id=${selectedSection}`)
      ]);
      setAssignments(assignmentsRes.data.results || assignmentsRes.data || []);
      setRoster(rosterRes.data.roster || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createAssignment = async () => {
    try {
      await api.post('/assignments/', {
        ...newAssignment,
        section: selectedSection
      });
      toast.success('Assignment created');
      setShowCreateDialog(false);
      setNewAssignment({ title: '', description: '', due_date: '', max_points: 100, assignment_type: 'project' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const deleteAssignment = async (id) => {
    if (!window.confirm('Delete this assignment?')) return;
    try {
      await api.delete(`/assignments/${id}/`);
      toast.success('Assignment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const openGradeDialog = async (assignment) => {
    setSelectedAssignment(assignment);
    try {
      const res = await api.get(`/assignments/${assignment.id}/submissions/`);
      setSubmissions(res.data || []);
      setShowGradeDialog(true);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const saveGrade = async (learnerId, points, feedback) => {
    try {
      await api.post(`/assignments/${selectedAssignment.id}/grade/`, {
        learner_id: learnerId,
        points_earned: points,
        feedback: feedback
      });
      toast.success('Grade saved');
      // Refresh submissions
      const res = await api.get(`/assignments/${selectedAssignment.id}/submissions/`);
      setSubmissions(res.data || []);
      fetchData();
    } catch (error) {
      toast.error('Failed to save grade');
    }
  };

  const sectionInfo = sections.find(s => s.id === selectedSection);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Assignments & Grading</h1>
          <p className="text-muted-foreground">Create assignments and grade student work</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Assignment
        </Button>
      </div>

      {/* Section Selector */}
      <Select value={selectedSection} onValueChange={setSelectedSection}>
        <SelectTrigger className="w-[300px]">
          <SelectValue placeholder="Select a class" />
        </SelectTrigger>
        <SelectContent>
          {sections.map(section => (
            <SelectItem key={section.id} value={section.id}>
              {section.course_title} - {section.location}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Assignments List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Assignments
            <Badge variant="outline">{assignments.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No assignments yet</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Assignment
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assignment</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-center">Points</TableHead>
                  <TableHead className="text-center">Submitted</TableHead>
                  <TableHead className="text-center">Graded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map(assignment => (
                  <TableRow key={assignment.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{assignment.title}</p>
                        {assignment.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1">{assignment.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {assignment.assignment_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {assignment.due_date ? (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {new Date(assignment.due_date).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">No due date</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{assignment.max_points}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {assignment.submissions_count}/{roster.length}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={assignment.graded_count === roster.length ? 'default' : 'secondary'}>
                        {assignment.graded_count}/{roster.length}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openGradeDialog(assignment)}
                        >
                          <Award className="h-4 w-4 mr-1" />
                          Grade
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteAssignment(assignment.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Assignment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newAssignment.title}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Assignment title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newAssignment.description}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Assignment description and instructions"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select 
                  value={newAssignment.assignment_type} 
                  onValueChange={(v) => setNewAssignment(prev => ({ ...prev, assignment_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project">Project</SelectItem>
                    <SelectItem value="homework">Homework</SelectItem>
                    <SelectItem value="quiz">Quiz</SelectItem>
                    <SelectItem value="participation">Participation</SelectItem>
                    <SelectItem value="presentation">Presentation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Points</Label>
                <Input
                  type="number"
                  value={newAssignment.max_points}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, max_points: parseInt(e.target.value) || 100 }))}
                />
              </div>
            </div>
            <div>
              <Label>Due Date (optional)</Label>
              <Input
                type="date"
                value={newAssignment.due_date}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createAssignment}>Create Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grading Dialog */}
      <Dialog open={showGradeDialog} onOpenChange={setShowGradeDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Grade: {selectedAssignment?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
              <div>
                <span className="text-sm text-muted-foreground">Max Points:</span>
                <Badge className="ml-2">{selectedAssignment?.max_points}</Badge>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Students:</span>
                <Badge variant="outline" className="ml-2">{roster.length}</Badge>
              </div>
            </div>
            
            <div className="space-y-3">
              {roster.map(student => {
                const submission = submissions.find(s => s.learner === student.learner_id);
                return (
                  <GradeRow
                    key={student.learner_id}
                    student={student}
                    submission={submission}
                    maxPoints={selectedAssignment?.max_points || 100}
                    onSave={(points, feedback) => saveGrade(student.learner_id, points, feedback)}
                  />
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GradeRow({ student, submission, maxPoints, onSave }) {
  const [points, setPoints] = useState(submission?.points_earned ?? '');
  const [feedback, setFeedback] = useState(submission?.feedback || '');
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(points === '' ? null : parseInt(points), feedback);
    setSaving(false);
    setExpanded(false);
  };

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-medium">{student.learner_name}</span>
            {submission?.status === 'graded' && (
              <Badge variant="secondary">
                {submission.points_earned}/{maxPoints}
                <span className="ml-1 text-xs">
                  ({submission.percentage}%)
                </span>
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!expanded && (
              <Input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="Points"
                className="w-20"
                max={maxPoints}
                min={0}
              />
            )}
            <span className="text-muted-foreground">/ {maxPoints}</span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              <FileText className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {expanded && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <Label className="w-16">Points:</Label>
              <Input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                max={maxPoints}
                min={0}
                className="w-24"
              />
              <span className="text-muted-foreground">/ {maxPoints}</span>
            </div>
            <div>
              <Label>Feedback</Label>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Feedback for student..."
                rows={2}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
